import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid2';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ordersApi from '../../../api/order';
import { ProductCategoryType } from '../../../common/enums';
import {
  mapProductToOrderItem
} from '../../../common/orderFunctions';
import { sortProducts } from '../../../common/productFunctions';
import { isStaff } from '../../../common/typeAssertionFunctions';
import { Order, OrderItem } from '../../../common/types';
import MenuTabs from '../../../components/menu/MenuTabs';
import OrderItemVariationsModal from '../../../components/modal/OrderItemVariationsModal';
import useAuthStore from '../../../datastore/useAuth';
import useProducts from '../../../datastore/useProducts';
import useStations from '../../../datastore/useStations';
import useAlert from '../../../hooks/useAlert';
import useStaffOrdering from '../../../hooks/useStaffOrdering';
import { getStaffDefaultStation } from '../functions/stationFunctions';
import { handleError } from '../../../api/requests';
import MenuCards from './components/MenuCards';
import MenuHeader from './components/MenuHeader';
import OrderFor from './components/OrderFor';

export default function OrderPage() {
  const { currentUser } = useAuthStore();
  const {
    products = [],
    loading: productsLoading,
    fetchProducts,
  } = useProducts();
  const { stations: stationData = [], loading: stationsLoading } =
    useStations();

  const productData = sortProducts(products.filter(f => f.isActive))

  const theme = useTheme();
  const { showAlert } = useAlert();
  const location = useLocation();
  const navigate = useNavigate();

  const {
    orderData,
    init,
    onVariationChange,
    onStationChange,
    onSizeChange,
    setPaymentType,
    clearOrder,
    removeOrderItem,
    addOrderItem,
  } = useStaffOrdering()

  const [searchProduct, setSearchProduct] = useState('');
  const [editOrderItem, setEditOrderItem] = useState<OrderItem | null>(null);
  const [selectedCategory, setSelectedCategory] = useState(
    ProductCategoryType.HotCoffee,
  );

  useEffect(() => {
    fetchProducts();
    init();
  }, []);


  const isLoading = productsLoading || stationsLoading;

  if (!isStaff(currentUser) || isLoading) return null;

  const { siteId: staffSiteId } = currentUser

  const orderStation = stationData.find(f => f.id === orderData.stationId)
  const staffStation = getStaffDefaultStation(currentUser, stationData)

  const assignedStation = orderStation || staffStation

  const walkUpReturnRoute: string = location.state?.returnRoute
    ? location.state.returnRoute
    : '';

  function handleCategoryChange(
    _: React.SyntheticEvent,
    newValue: ProductCategoryType,
  ) {
    setSelectedCategory(newValue);
  }

  function handleStationChange(stationName: string) {
    const stationId = stationData?.find(
      (station) => station.name === stationName,
    )?.id;

    if (!stationId) {
      showAlert(
        'Could not assign to station, please try again or revise manually after order is placed.',
        'warning',
      );
      console.info(
        `No station id found for station name: ${stationName}, please try again.`,
      );
      return;
    }

    onStationChange(stationId);
  }

  function handleRemoveOrderItem(id: number) {
    removeOrderItem(id)
    showAlert('Item removed from order', 'success');
  }

  function handleAddOrderItem(item: OrderItem) {
    addOrderItem(item)
  }

  function handleSizeChange(id: number, newSizeId: number) {
    onSizeChange(id, newSizeId, productData)
  }

  function handleSubmitVariationModal(updatedOrderItem: OrderItem | null) {
    console.log(updatedOrderItem)
    if (!updatedOrderItem) {
      setEditOrderItem(null);
      return;
    }

    onVariationChange(updatedOrderItem)
    setEditOrderItem(null)
  }

  function handleShowEditModal(id: number) {
    const orderItem = orderData.orderItems.find((item) => item.id === id);
    if (orderItem) {
      setEditOrderItem(orderItem);
    }
  }

  function onAddOrderItem(productId: number) {
    const product = productData?.find((product) => product.id === productId);
    if (product === undefined) {
      showAlert('Failed to add item, try again', 'warning');
      return;
    }
    const orderItem = mapProductToOrderItem(product);

    setEditOrderItem(orderItem);
  }

  function prepareOrderDataForSubmission(
    orderData: Order,
    customerName: string,
  ): Order {
    const order = { ...orderData };
    order.customerName = customerName;
    order.requestedPickupTime = new Date();
    order.orderItems.forEach((item) => {
      if (item.id < 0) {
        item.id = 0;
      }
    });
    order.siteId = staffSiteId || 0
    if (!order.stationId) {
      order.stationId = assignedStation.id
    }
    return order;
  }

  async function handleSubmitOrder(customerName: string) {
    const preparedData = prepareOrderDataForSubmission(orderData, customerName);
    try {
      await ordersApi.create(preparedData);
      showAlert('Order submitted', 'success');
      clearOrder();
    } catch (err) {
      const { message } = handleError(err)
      showAlert([
        'Submit order failed',
        message || 'server error'
      ].join(': '), 'error')
    }
    if (walkUpReturnRoute) {
      navigate(walkUpReturnRoute);
    }
  }

  return (
    <Grid
      container
      sx={{
        backgroundColor: theme.palette.common.white,
        height: '100vh',
      }}
    >
      <Grid
        size={{ xs: 12, md: 7 }}
        sx={{
          padding: theme.spacing(2),
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <MenuHeader
          searchProduct={searchProduct}
          setSearchProduct={setSearchProduct}
        />
        <Divider sx={{ paddingBottom: 1 }} />
        <Stack>
          <MenuTabs
            selectedCategory={selectedCategory}
            handleCategoryChange={handleCategoryChange}
          />
          <MenuCards
            isLoading={isLoading}
            productData={productData}
            searchProduct={searchProduct}
            orderItems={orderData.orderItems}
            selectedCategory={selectedCategory}
            addOrderItem={onAddOrderItem}
          />
        </Stack>
      </Grid>
      {
        assignedStation ? (
          <OrderFor
            orderData={orderData}
            products={productData ?? []}
            assignedStation={assignedStation}
            removeOrderItem={handleRemoveOrderItem}
            changeOrderItemSize={handleSizeChange}
            changeOrderStation={handleStationChange}
            editOrderItemVariations={handleShowEditModal}
            setPaymentType={setPaymentType}
            submitOrder={handleSubmitOrder}
            addOrderItem={handleAddOrderItem}
            onCancelOrder={clearOrder}
          />
        ) : (<Typography>Could not load stations :(</Typography>)
      }
      {!!editOrderItem && (
        <OrderItemVariationsModal
          item={editOrderItem}
          onClose={handleSubmitVariationModal}
          submitText="Save Changes"
        />
      )}
    </Grid>
  );
}
